import dayjs from 'dayjs';
import { formatDate } from '@frontend/date';
import { FormValues } from '@frontend/design-system';
import { DATETIME_FORMATS } from '../../../constants';
import type { ActivitySourceType, ActivityType, StatusType } from '../types';
import { FILTER_VALUE_DELIMITER } from './constants';
import { FiltersFormConfig } from './filters-drawer';
import type { FilterState } from './types';

export const toFormValues = (filters: FilterState): FormValues<FiltersFormConfig> => ({
  activityTypes: [...filters.activityTypes],
  appointmentTypeIds: [...filters.appointmentTypeIds],
  sourceTypes: [...filters.sourceTypes],
  statusTypes: [...filters.statusTypes],
  dateRange: [filters.startDate, filters.endDate],
});

export const normalizeFilters = (values: FormValues<FiltersFormConfig>): FilterState => ({
  activityTypes: [...((values.activityTypes ?? []) as ActivityType[])],
  appointmentTypeIds: [...(values.appointmentTypeIds ?? [])],
  sourceTypes: [...((values.sourceTypes ?? []) as ActivitySourceType[])],
  statusTypes: [...((values.statusTypes ?? []) as StatusType[])],
  startDate: values.dateRange?.[0] || '',
  endDate: values.dateRange?.[1] || '',
});

// Expands grouped filter option values into the flat, de-duplicated list expected by the API.
// Values that share a label/badge are stored as a single delimited option value.
export const expandFilterValues = (values: string[]): string[] => [
  ...new Set(values.flatMap((value) => value.split(FILTER_VALUE_DELIMITER))),
];

export const getTimeFrame = (startDate: string, endDate: string) => {
  if (!startDate || !endDate) return undefined;

  return {
    start: formatDate(startDate, DATETIME_FORMATS.ISO_DATETIME_WITH_MILLISECONDS_UTC),
    end: formatDate(endDate, DATETIME_FORMATS.ISO_DATETIME_WITH_MILLISECONDS_UTC),
  };
};

export const hasActiveFilters = (filters: FilterState) =>
  Boolean(
    filters.activityTypes.length ||
      filters.appointmentTypeIds.length ||
      filters.sourceTypes.length ||
      filters.statusTypes.length ||
      filters.startDate ||
      filters.endDate
  );

export const formatAppliedFilterDate = (value: string) => {
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('MMM D, YYYY') : value;
};
